import React, { useEffect, useMemo, useState } from "react";
import { Box, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ActionModal, NoticeboardButton } from "../shared";
import {
  combinePublishAtParts,
  parsePublishAtParts,
  getPublishAtValidationError,
} from "../../utils/noticeScheduling";
import { getEventStartDateTimeMs } from "../../utils/eventDateValidation";
import {
  EVENT_DATE_PICKER_FORMAT,
  EVENT_DATE_PICKER_LOCALE,
} from "../../utils/eventDateDisplay";
import type { Notice } from "../../services/api/types";

type RescheduleDialogProps = {
  open: boolean;
  notice: Notice | null;
  saving?: boolean;
  onClose: () => void;
  onSave: (notice: Notice, publishAt: string) => void;
};

const RescheduleDialog: React.FC<RescheduleDialogProps> = ({
  open,
  notice,
  saving = false,
  onClose,
  onSave,
}) => {
  const [publishDate, setPublishDate] = useState<Date | null>(null);
  const [publishTime, setPublishTime] = useState("");

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  useEffect(() => {
    if (open && notice) {
      const parts = parsePublishAtParts(notice.publishAt);
      setPublishDate(parts.date);
      setPublishTime(parts.time);
    }
  }, [open, notice]);

  const handleSave = () => {
    if (!notice) return;
    const publishAt = combinePublishAtParts(publishDate, publishTime);
    if (!publishAt.trim()) return;
    const error = getPublishAtValidationError(publishAt, {
      eventStartDate: notice.eventStartDate,
      eventStartTime: notice.eventStartTime,
    });
    if (error) return;
    onSave(notice, new Date(publishAt).toISOString());
  };

  const publishAtPreview = combinePublishAtParts(publishDate, publishTime);
  const validationError = publishAtPreview
    ? getPublishAtValidationError(publishAtPreview, {
        eventStartDate: notice?.eventStartDate,
        eventStartTime: notice?.eventStartTime,
      })
    : undefined;
  const isValidTime = Boolean(publishAtPreview) && !validationError;

  const maxPublishDate = useMemo(() => {
    const startMs = getEventStartDateTimeMs(
      notice?.eventStartDate,
      notice?.eventStartTime,
    );
    if (startMs === null) return undefined;
    const d = new Date(startMs);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [notice?.eventStartDate, notice?.eventStartTime]);

  return (
    <ActionModal
      open={open}
      onClose={onClose}
      title="Reschedule notice"
      subtitle="Choose a new publish date and time"
      maxWidth="sm"
      disableBackdropClick={saving}
      footer={
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <NoticeboardButton
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </NoticeboardButton>
          <NoticeboardButton
            variant="primary"
            onClick={handleSave}
            loading={saving}
            disabled={!isValidTime}
          >
            Save
          </NoticeboardButton>
        </Box>
      }
    >
      <Typography variant="body1" sx={{ mb: 2.5, lineHeight: 1.6 }}>
        Choose when{" "}
        <Typography component="span" fontWeight={600}>
          &quot;{notice?.title}&quot;
        </Typography>{" "}
        should go live.
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={EVENT_DATE_PICKER_LOCALE}
        >
          <DatePicker
            label="Publish date"
            format={EVENT_DATE_PICKER_FORMAT}
            value={publishDate}
            minDate={today}
            maxDate={maxPublishDate}
            disabled={saving}
            onChange={setPublishDate}
            slotProps={{
              textField: {
                fullWidth: true,
                size: "small",
              },
            }}
          />
        </LocalizationProvider>
        <TextField
          type="time"
          label="Publish time"
          value={publishTime}
          onChange={(e) => setPublishTime(e.target.value)}
          disabled={saving}
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
          error={Boolean(publishAtPreview) && !isValidTime}
          helperText={
            publishAtPreview && !isValidTime ? validationError : undefined
          }
        />
      </Box>
    </ActionModal>
  );
};

export default RescheduleDialog;
